/* GrowthOS — Activity Timeline (shared across all module view pages).
   Ported from design growthos/LeadRecord3 Activities. Reads the audit data from
   apiServices.getModuleTimeline → timelinelist (old/current JSON snapshots) and
   renders each change as a timeline entry with a minicard of field diffs. */
import { useCallback, useEffect, useState } from 'react';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { DateFormatter, NameFormatter } from '../../shared/controls/formatters';
import TruncatedText from '../../shared/component/TruncateText';
import { Ic } from "../../shared/component/GosIcon";


function getDifferences(oldData, newData) {
  const diffs = {};
  for (const key in newData) {
    if (newData.hasOwnProperty(key)) {
      const oldValue = oldData[key];
      const newValue = newData[key];
      if (oldValue != newValue) {
        const isDateField = key.toLowerCase().includes('date');
        diffs[key] = {
          old: isDateField ? DateFormatter(oldValue) : oldValue,
          new: isDateField ? DateFormatter(newValue) : newValue,
        };
      }
    }
  }
  return diffs;
}

const EXCLUDE = new Set([
  'UpdatedDateTime', 'DomainEvents', 'AuditableRequestName', 'TagsRaw', 'FullName', 'UpdatedBy',
]);
const isShownKey = (key) =>
  !EXCLUDE.has(key) && (!key.toLowerCase().endsWith('id') || key === 'SkypeId');

const humanize = (key) =>
  String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

const fmtTime = (val) => {
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const fmtDateLabel = (val) => {
  const d = new Date(val);
  if (isNaN(d.getTime())) return 'Earlier';
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOf(today) - startOf(d)) / 86400000);
  const md = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (dayDiff === 0) return `Today · ${md}`;
  if (dayDiff === 1) return `Yesterday · ${md}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Timeline = ({ localData, module = 'Lead' }) => {
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState([]);

  const fetchData = useCallback(async () => {
    if (!localData?.id) return;
    setLoading(true);
    try {
      const response = await apiServices.getModuleTimeline({ moduleType: module, moduleId: localData.id });
      if (response.status === 200) {
        setHistoryData(response.data.timelinelist || []);
      } else {
        toast.error(`An unexpected error occurred: ${response.status}`, { position: 'top-right' });
      }
    } catch (error) {
      toast.error('An unexpected error occurred.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  }, [localData?.id, module]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groups = [];
  const byDate = {};
  historyData.forEach((item) => {
    const oldData = JSON.parse(item.oldJson || '{}');
    const currentData = JSON.parse(item.currentJson || '{}');
    const createdAt = currentData.UpdatedDateTime || oldData.UpdatedDateTime || null;
    const dateKey = createdAt ? new Date(createdAt).toLocaleDateString() : 'Unknown';
    const diffs = getDifferences(oldData, currentData);
    const changes = Object.keys(diffs).filter(isShownKey).map((key) => ({ key, ...diffs[key] }));
    const entry = {
      time: createdAt ? fmtTime(createdAt) : '',
      ts: createdAt ? new Date(createdAt).getTime() : 0,
      user: currentData.UpdatedBy || 'Unknown User',
      changes,
    };
    if (!byDate[dateKey]) {
      byDate[dateKey] = { label: fmtDateLabel(createdAt), ts: entry.ts, items: [] };
      groups.push(byDate[dateKey]);
    }
    byDate[dateKey].items.push(entry);
    byDate[dateKey].ts = Math.max(byDate[dateKey].ts, entry.ts);
  });
  groups.sort((a, b) => b.ts - a.ts);
  groups.forEach((g) => g.items.sort((a, b) => b.ts - a.ts));

  return (
    <div className="gos-lr-card gos-lr-act">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="clock" size={16} /> Activity Timeline
        </div>
        <div className="gos-lr-act-tools">
          <button type="button" className="gos-lr-ghost-btn gos-lr-btn-xs" disabled>
            <Ic name="filter" size={14} /> Filter
          </button>
          <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={fetchData} title="Refresh">
            <Ic name="refresh" size={15} />
          </button>
        </div>
      </div>

      <div className="gos-lr-act-body">
        {loading ? (
          <div className="gos-lr-act-empty">Loading timeline…</div>
        ) : groups.length === 0 ? (
          <div className="gos-lr-act-empty">
            <div className="gos-lr-ph-ic">
              <Ic name="clock" size={22} />
            </div>
            <div className="gos-lr-ph-title">No activity yet</div>
            <div className="gos-lr-ph-text">Changes and updates to this record will show up here.</div>
          </div>
        ) : (
          groups.map((g, gi) => (
            <div className="gos-lr-tl-group" key={gi}>
              <span className="gos-lr-tl-date">{g.label}</span>
              <div className="gos-lr-tl">
                {g.items.map((entry, i) => (
                  <div className="gos-lr-tl-item" key={i}>
                    <div className="gos-lr-tl-time">{entry.time}</div>
                    <div className="gos-lr-tl-track">
                      <div className="gos-lr-tl-dot">
                        <Ic name="edit" size={15} />
                      </div>
                    </div>
                    <div className="gos-lr-tl-body">
                      <div className="gos-lr-tl-head">
                        <span className="gos-lr-tl-title">Record updated</span>
                        <span className="gos-lr-tl-sub">by {NameFormatter(entry.user) || 'Unknown User'}</span>
                      </div>
                      {entry.changes.length > 0 ? (
                        <div className="gos-lr-mini">
                          <div className="gos-lr-mini-head">
                            {entry.changes.length} field{entry.changes.length > 1 ? 's' : ''} changed
                          </div>
                          <div className="gos-lr-mini-body">
                            {entry.changes.map((c) => (
                              <div className="gos-lr-drow" key={c.key}>
                                <div className="gos-lr-dlabel">{humanize(c.key)}</div>
                                <div className="gos-lr-dval gos-lr-diff">
                                  <span className="gos-lr-diff-old">
                                    <TruncatedText value={c.old || '—'} limit={40} />
                                  </span>
                                  <Ic name="arrow" size={13} className="gos-lr-diff-arrow" />
                                  <span className="gos-lr-diff-new">
                                    <TruncatedText value={c.new || '—'} limit={40} />
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="gos-lr-tl-note">No field-level changes recorded.</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Timeline;
